import React, { useState, useEffect, useContext } from 'react';
import { Container, Form, Button, Card, Row, Col, Alert } from 'react-bootstrap';
import { useNavigate, useParams } from 'react-router-dom';
import { AuthContext } from '../../contexts/AuthContext';
import api from '../../services/api';
import './Customer.css';

const CustomerForm = () => {
  const { custCode } = useParams(); // 如果是編輯模式，會有custCode
  const { user } = useContext(AuthContext);
  const navigate = useNavigate();
  
  // 表單狀態
  const [custCodeInput, setCustCodeInput] = useState('');
  const [custName, setCustName] = useState('');
  const [parent, setParent] = useState('');
  const [addr1, setAddr1] = useState('');
  const [addr2, setAddr2] = useState('');
  const [addr3, setAddr3] = useState('');
  
  // 頁面狀態
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  
  // 可用的上級客戶列表
  const [parentCustomers, setParentCustomers] = useState([]);
  
  // 是否為編輯模式
  const isEditMode = !!custCode;
  
  // 確保只有管理員可以訪問
  useEffect(() => {
    if (user?.role !== 0) {
      navigate('/');
    }
  }, [user, navigate]);
  
  // 載入上級客戶選項
  useEffect(() => {
    if (user?.role === 0) {
      loadParentCustomers();
    }
  }, [user]);
  
  // 如果是編輯模式，載入客戶資料
  useEffect(() => {
    if (isEditMode && user?.role === 0) {
      loadCustomerData();
    }
  }, [isEditMode, user, custCode]);
  
  const loadParentCustomers = async () => {
    try {
      const response = await api.get('/customers');
      setParentCustomers(response.data.data || []);
    } catch (err) {
      console.error('載入上級客戶失敗:', err);
    }
  };
  
  const loadCustomerData = async () => {
    setLoading(true);
    try {
      const response = await api.get(`/customers/${custCode}`);
      const customerData = response.data.data.customer;
      
      setCustCodeInput(customerData.cust_code);
      setCustName(customerData.cust_name || '');
      setParent(customerData.parent || '');
      setAddr1(customerData.addr1 || '');
      setAddr2(customerData.addr2 || '');
      setAddr3(customerData.addr3 || '');
      
      setError('');
    } catch (err) {
      console.error('載入客戶資料失敗:', err);
      setError('載入客戶資料失敗: ' + (err.response?.data?.message || '請稍後再試'));
    } finally {
      setLoading(false);
    }
  };
  
  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // 表單驗證
    if (!custCodeInput) {
      setError('請輸入客戶編號');
      return;
    }
    
    if (!custName) {
      setError('請輸入客戶名稱');
      return;
    }
    
    setSaving(true);
    setError('');
    setSuccess('');
    
    try {
      const customerData = {
        cust_code: custCodeInput,
        cust_name: custName,
        parent,
        addr1,
        addr2,
        addr3
      };
      
      if (isEditMode) {
        // 更新現有客戶
        await api.put(`/customers/${custCode}`, customerData);
        setSuccess('客戶已成功更新');
      } else {
        // 創建新客戶
        await api.post('/customers', customerData);
        setSuccess('客戶已成功創建');
        
        // 清空表單（如果不是編輯模式）
        if (!isEditMode) {
          setCustCodeInput('');
          setCustName('');
          setParent('');
          setAddr1('');
          setAddr2('');
          setAddr3('');
        }
      }
    } catch (err) {
      console.error('保存客戶失敗:', err);
      setError('保存客戶失敗: ' + (err.response?.data?.message || '請稍後再試'));
    } finally {
      setSaving(false);
    }
  };
  
  const handleCancel = () => {
    navigate('/customers');
  };
  
  return (
    <Container className="customer-form-container">
      <h2 className="mb-4">{isEditMode ? '編輯客戶' : '新增客戶'}</h2>
      
      {error && <Alert variant="danger">{error}</Alert>}
      {success && <Alert variant="success">{success}</Alert>}
      
      {loading ? (
        <div className="text-center my-5">
          <div className="spinner-border text-primary" role="status">
            <span className="visually-hidden">載入中...</span>
          </div>
        </div>
      ) : (
        <Card>
          <Card.Body>
            <Form onSubmit={handleSubmit}>
              <Row>
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label>客戶編號</Form.Label>
                    <Form.Control 
                      type="text" 
                      value={custCodeInput} 
                      onChange={(e) => setCustCodeInput(e.target.value)}
                      disabled={isEditMode} // 編輯模式下不允許修改客戶編號
                      required
                    />
                  </Form.Group>
                </Col>
                
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label>客戶名稱</Form.Label>
                    <Form.Control 
                      type="text" 
                      value={custName} 
                      onChange={(e) => setCustName(e.target.value)}
                      required
                    />
                  </Form.Group>
                </Col>
              </Row>
              
              <Row>
                <Col md={12}>
                  <Form.Group className="mb-3">
                    <Form.Label>上級客戶</Form.Label>
                    <Form.Select 
                      value={parent} 
                      onChange={(e) => setParent(e.target.value)}
                    >
                      <option value="">無上級客戶</option>
                      {parentCustomers.map(pc => (
                        <option key={pc.cust_code} value={pc.cust_code}>
                          {pc.cust_name} ({pc.cust_code})
                        </option>
                      ))}
                    </Form.Select>
                  </Form.Group>
                </Col>
              </Row>
              
              <Row>
                <Col md={12}>
                  <Form.Group className="mb-3">
                    <Form.Label>地址</Form.Label>
                    <Form.Control 
                      type="text" 
                      placeholder="地址第1行"
                      value={addr1} 
                      onChange={(e) => setAddr1(e.target.value)}
                      className="mb-2"
                    />
                    <Form.Control 
                      type="text" 
                      placeholder="地址第2行"
                      value={addr2} 
                      onChange={(e) => setAddr2(e.target.value)}
                      className="mb-2"
                    />
                    <Form.Control 
                      type="text" 
                      placeholder="地址第3行"
                      value={addr3} 
                      onChange={(e) => setAddr3(e.target.value)}
                    />
                  </Form.Group>
                </Col>
              </Row>
              
              <div className="d-flex justify-content-between mt-4">
                <Button 
                  variant="outline-secondary" 
                  onClick={handleCancel}
                  disabled={saving}
                >
                  取消
                </Button>
                
                <Button 
                  variant="primary" 
                  type="submit" 
                  disabled={saving}
                >
                  {saving ? '保存中...' : '保存'}
                </Button>
              </div>
            </Form>
          </Card.Body>
        </Card>
      )}
    </Container>
  );
};

export default CustomerForm;